import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { OwlApi } from "../lib/api";
import type { FromNyvoResponse, NyvoIssue, NyvoToolBox } from "../lib/types";

/**
 * Owl's home surface (7 Aug 2026 — replaced the daily cards, founder call).
 *
 * The latest NYVO Weekly issue leads, three earlier issues follow, then three
 * NYVO tool boxes and one explore link. Every word and image comes from
 * nyvo.money's own pages via the server-side sync — this component invents
 * no copy. Chat remains the product underneath.
 */

function formatLongDate(d: Date): string {
  return d.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatIssueDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const label = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  const today = new Date();
  const isToday =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  return isToday ? `Today · ${label}` : label;
}

const TINT: Record<string, { box: string; name: string }> = {
  sky: { box: "bg-sky-50 hover:border-sky-300", name: "text-sky-700" },
  violet: { box: "bg-violet-50 hover:border-violet-300", name: "text-violet-700" },
  green: { box: "bg-green-50 hover:border-green-300", name: "text-green-700" },
};

export function FromNyvo({
  onOpenChat,
}: {
  onOpenChat: (prefill?: string) => void;
}) {
  const [data, setData] = useState<FromNyvoResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    OwlApi.fromNyvo()
      .then((response) => {
        if (alive) setData(response);
      })
      .catch(() => {
        if (alive) setError("Owl is catching up, check back in a moment.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return <HomeSkeleton />;

  if (error || !data || !data.available || !data.latest) {
    return (
      <section id="today" className="pt-2">
        <Masthead />
        <div className="card mt-4 p-8 text-center text-slate-500">
          {error ?? "The latest from NYVO is on its way. Back shortly."}
        </div>
      </section>
    );
  }

  return (
    <section id="today" className="pt-2">
      <Masthead />
      <div className="mt-4">
        <HeroIssue issue={data.latest} />

        {data.earlier.length > 0 && (
          <>
            <SectionLabel>Earlier issues</SectionLabel>
            <div className="card overflow-hidden p-0">
              {data.earlier.map((issue) => (
                <IssueRow key={issue.url} issue={issue} />
              ))}
            </div>
          </>
        )}

        {data.tools.length > 0 && (
          <>
            <SectionLabel>Explore NYVO</SectionLabel>
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
              {data.tools.map((tool) => (
                <ToolBox key={tool.url} tool={tool} />
              ))}
            </div>
          </>
        )}

        <div className="mt-6 flex justify-center">
          <a
            href={data.explore_url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-sky-200 bg-white px-6 py-2.5 text-[13.5px] font-bold text-sky-700 transition hover:bg-sky-50"
          >
            Explore more at nyvo.money →
          </a>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-[18px] bg-nyvo-950 px-5 py-4 text-slate-200">
          <div>
            <p className="text-[14.5px] font-bold text-white">
              Have a question about a fund or the market?
            </p>
            <p className="mt-0.5 text-[12px] text-slate-400">
              Owl answers with NYVO's own research – ratings, holdings, costs.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChat()}
            className="inline-flex items-center gap-1.5 rounded-full bg-sky-400 px-5 py-2 text-[13px] font-extrabold text-nyvo-950 transition hover:bg-sky-300"
          >
            Ask Owl
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <p className="mt-5 px-1 text-center text-[11.5px] leading-relaxed text-slate-400">
          The newsletter and tools live on nyvo.money. Nothing here is a
          recommendation to buy or sell.
        </p>
      </div>
    </section>
  );
}

function Masthead() {
  return (
    <div className="flex items-baseline justify-between px-1.5 pb-1">
      <div className="text-[13px] font-extrabold tracking-[0.14em] text-slate-700">
        OWL <span className="text-sky-500">·</span> FROM NYVO
      </div>
      <div className="text-[13px] text-slate-500">
        {formatLongDate(new Date())}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div className="mx-1.5 mb-2.5 mt-7 text-[11px] font-extrabold uppercase tracking-[0.13em] text-slate-500">
      {children}
    </div>
  );
}

function HeroIssue({ issue }: { issue: NyvoIssue }) {
  const date = formatIssueDate(issue.published_at);
  return (
    <a
      href={issue.url}
      target="_blank"
      rel="noopener noreferrer"
      className="card card-hover block overflow-hidden p-0"
    >
      {issue.image && (
        <img
          src={issue.image}
          alt=""
          className="block aspect-2/1 w-full object-cover"
        />
      )}
      <div className="px-5 pb-5 pt-4 sm:px-6">
        <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
          <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-sky-700">
            NYVO Weekly
          </span>
          {issue.read_minutes != null && (
            <span className="text-[11px] text-slate-400">
              {issue.read_minutes} min read
            </span>
          )}
          {date && (
            <span className="ml-auto font-mono text-[11px] text-slate-400">
              {date}
            </span>
          )}
        </div>
        <h2 className="font-display text-[22px] font-bold leading-tight tracking-[-0.015em] text-nyvo-900 sm:text-[24px]">
          {issue.title}
        </h2>
        {issue.summary && (
          <p className="mt-2 max-w-[68ch] text-[14.5px] leading-[1.6] text-slate-600">
            {issue.summary}
          </p>
        )}
        <span className="mt-4 inline-block rounded-full bg-sky-100 px-4 py-2 text-[13px] font-bold text-sky-700">
          Read the issue →
        </span>
      </div>
    </a>
  );
}

function IssueRow({ issue }: { issue: NyvoIssue }) {
  const date = formatIssueDate(issue.published_at);
  return (
    <a
      href={issue.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 border-b border-slate-100 px-5 py-4 transition last:border-b-0 hover:bg-slate-50"
    >
      <span className="text-[14px] font-semibold leading-[1.35] text-slate-800">
        {issue.title}
      </span>
      {date && (
        <span className="ml-auto whitespace-nowrap font-mono text-[11px] text-slate-400">
          {date}
        </span>
      )}
      <span className="font-bold text-sky-500">→</span>
    </a>
  );
}

function ToolBox({ tool }: { tool: NyvoToolBox }) {
  const tint = TINT[tool.tint] ?? TINT.sky;
  return (
    <a
      href={tool.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`rounded-2xl border border-slate-200 p-4 transition hover:-translate-y-0.5 ${tint.box}`}
    >
      <div className={`text-[14.5px] font-extrabold ${tint.name}`}>
        {tool.name}
      </div>
      <div className="mt-1 text-[11.5px] leading-normal text-slate-600">
        {tool.line}
      </div>
    </a>
  );
}

function HomeSkeleton() {
  return (
    <section id="today" className="pt-2">
      <div className="h-[26px] animate-pulse rounded-lg bg-slate-100" />
      <div className="mt-4 h-[420px] animate-pulse rounded-[20px] bg-slate-100" />
      <div className="mt-6 h-[170px] animate-pulse rounded-[18px] bg-slate-100" />
    </section>
  );
}
